import { useCallback, useMemo } from 'react';
import { useFeatureFlag } from 'featureFlags';
import { useAppSelector } from 'hooks';
import { getAvatarUrl } from 'utils/media';

import { deriveStreakView } from 'components/Streaks/model/deriveView';
import { streakStore, useStreakSnapshot } from 'components/Streaks/state/streakStore';
import { useStatusAvatars } from 'components/Streaks/state/useStatusAvatars';
import { useStreakClock } from 'components/Streaks/state/useStreakClock';
import { useStreakReducedMotion } from 'components/Streaks/useStreakReducedMotion';

import StreakBanner from './StreakBanner';
import { BannerHeight } from './stubGeometry';

/**
 * True when the lobby should show the streak banner: flag on and a snapshot
 * loaded. The sync hook only reads one for a signed-in player and clears it
 * when the player changes, so a guest never has one.
 */
export function useStreakBannerVisible(): boolean {
  const enabled = useFeatureFlag('streaks');
  const snapshot = useStreakSnapshot();
  return enabled && !!snapshot;
}

/** Opens the account panel with the streak card expanded (EN-11155 AC 5). */
export function openStreakPanel() {
  streakStore.requestExpanded();
  window.dispatchEvent(new CustomEvent('open-profile-panel'));
}

export default function LobbyStreakBanner({ height }: { height: BannerHeight }) {
  const visible = useStreakBannerVisible();
  const snapshot = useStreakSnapshot();
  const avatar = useAppSelector((s) => s.user.playerAvatar);
  const reducedMotion = useStreakReducedMotion();
  const now = useStreakClock(visible);
  const statusAvatars = useStatusAvatars(visible);
  const view = useMemo(
    () => (snapshot ? deriveStreakView(snapshot, now, statusAvatars) : null),
    [snapshot, now, statusAvatars]
  );
  const avatarSrc = useMemo(() => (avatar ? getAvatarUrl(avatar) : undefined), [avatar]);
  const onOpen = useCallback(() => openStreakPanel(), []);
  if (!visible || !view) return null;
  return (
    <StreakBanner
      view={view}
      height={height}
      avatarSrc={avatarSrc}
      statusAvatars={statusAvatars}
      reducedMotion={reducedMotion}
      onOpen={onOpen}
    />
  );
}
